import React, { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { useTranslation } from 'react-i18next'
import { ALLOW_IN_MVP } from '../../config/environment'
import { DMSUserProfile, profileService, TotpSettings } from '../../services/profile.service'
import { toastService } from '../../services/toast.service'
import { copyToClipboard } from '../../utils/clipboard'
import { parseBackendErrors } from '../../utils/errorHandler'
import { useModuleFlag } from '../../utils/featureFlags'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { Phase2NoticeModal } from '../common/Phase2NoticeModal'
import './ProfileEditModal.scss'

const DEFAULT_CONFIRM_DIALOG: {
  show: boolean
  title: string
  message: string
  confirmText: string
  variant: 'danger' | 'warning' | 'primary'
  onConfirm: () => void | Promise<void>
} = {
  show: false,
  title: '',
  message: '',
  confirmText: '',
  variant: 'danger',
  onConfirm: () => {},
}

interface ProfileEditModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<DMSUserProfile | null>(null)
  const [formData, setFormData] = useState({
    email: '',
    email_confirm: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    auth_token: '',
  })
  const [showEmailConfirm, setShowEmailConfirm] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<typeof DEFAULT_CONFIRM_DIALOG>(DEFAULT_CONFIRM_DIALOG)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [revealPassword, setRevealPassword] = useState(false)
  const [revealPasswordConfirm, setRevealPasswordConfirm] = useState(false)

  // TOTP (2FA)
  const [isTotpEnabled, setIsTotpEnabled] = useState(false)
  const [totpSettings, setTotpSettings] = useState<TotpSettings | null>(null)
  const [totpSettingsLoading, setTotpSettingsLoading] = useState(false)
  const [totpLoading, setTotpLoading] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [totpOpen, setTotpOpen] = useState(false)
  const [showTotpPhase2Modal, setShowTotpPhase2Modal] = useState(false)
  const isExternalIntegrationsModuleEnabled = useModuleFlag('external_integrations', true)
  const [hasAuthToken, setHasAuthToken] = useState(false)
  const [authTokenLoading, setAuthTokenLoading] = useState(false)

  const openConfirmDialog = (dialog: Partial<typeof DEFAULT_CONFIRM_DIALOG>) => {
    setConfirmDialog({
      ...DEFAULT_CONFIRM_DIALOG,
      ...dialog,
      show: true,
      onConfirm: dialog.onConfirm || DEFAULT_CONFIRM_DIALOG.onConfirm,
    })
  }

  const closeConfirmDialog = () => {
    setConfirmDialog(DEFAULT_CONFIRM_DIALOG)
  }

  const handleDialogConfirm = () => {
    let result: void | Promise<void> | undefined
    try {
      result = confirmDialog.onConfirm()
    } catch (error) {
      closeConfirmDialog()
      return
    }
    if (result instanceof Promise) {
      result.finally(() => closeConfirmDialog())
    } else {
      closeConfirmDialog()
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadProfile()
    }
  }, [isOpen, isExternalIntegrationsModuleEnabled])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const profileData = await profileService.get()
      setProfile(profileData)
      setIsTotpEnabled(!!profileData.is_mfa_enabled)
      setHasAuthToken(Boolean(profileData.has_auth_token))
      // Čuvamo originalnu vrednost password-a kao što radi React
      setFormData({
        email: profileData.email || '',
        email_confirm: '',
        password: '',
        password_confirm: '',
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        auth_token: '',
      })
    } catch (error) {
      toastService.showError(t('error_loading_profile'))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }

    if (field === 'email' && value !== profile?.email) {
      setShowEmailConfirm(true)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setErrors({})

      // Basic validation
      const newErrors: Record<string, string> = {}

      const shouldValidatePassword = formData.password || formData.password_confirm
      if (shouldValidatePassword) {
        if (!formData.password) {
          newErrors.password = t('error_field_required')
        }
        if (!formData.password_confirm) {
          newErrors.password_confirm = t('error_field_required')
        } else if (formData.password !== formData.password_confirm) {
          newErrors.password_confirm = t('passwords_do_not_match')
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return
      }
      const updateData: Partial<DMSUserProfile> = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
      }

      if (showEmailConfirm && formData.email_confirm) {
        // @ts-ignore
        updateData.email_confirm = formData.email_confirm as any
      }

      if (formData.password) {
        updateData.password = formData.password
      }

      await profileService.update(updateData)

      toastService.showSuccess(t('profile_updated_successfully'))

      if (formData.password) {
        toastService.showInfo(t('password_changed_logout_warning'))
        // Redirect to logout after a delay
        setTimeout(() => {
          window.location.href = '/accounts/logout/?next=/accounts/login/?next=/'
        }, 2500)
      } else {
        onClose()
      }
    } catch (error) {
      const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving_profile'))
      setErrors(fieldErrors)
      toastService.showError(t('error_saving_profile'), errorResponse)
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateToken = () => {
    if (!isExternalIntegrationsModuleEnabled) {
      return
    }

    openConfirmDialog({
      title: t('generate_new_token_question'),
      message: t('generate_new_token_warning'),
      confirmText: t('generate_new_token'),
      variant: 'warning',
      onConfirm: async () => {
        if (hasAuthToken && !formData.auth_token) {
          toastService.showInfo(t('fetch_token_first'))
          return
        }
        try {
          const newToken = await profileService.generateAuthToken()
          setFormData((prev) => ({ ...prev, auth_token: newToken }))
          setHasAuthToken(true)
          toastService.showSuccess(t('auth_token_generated'))
        } catch (error) {
          toastService.showError(t('error_generating_token'))
        }
      },
    })
  }

  const handleShowToken = async () => {
    if (!isExternalIntegrationsModuleEnabled) {
      return
    }
    try {
      setAuthTokenLoading(true)
      const response = await profileService.getAuthToken()
      setHasAuthToken(Boolean(response.hasToken))
      if (response.token) {
        setFormData((prev) => ({ ...prev, auth_token: response.token ?? '' }))
      } else {
        setFormData((prev) => ({ ...prev, auth_token: '' }))
        toastService.showInfo(t('no_token_generated'))
      }
    } catch (error) {
      toastService.showError(t('error_loading_profile'))
    } finally {
      setAuthTokenLoading(false)
    }
  }

  // TOTP handlers
  const loadTotp = async () => {
    try {
      setTotpSettingsLoading(true)
      const settings = await profileService.getTotpSettings()
      setTotpSettings(settings)
    } catch (error) {
      toastService.showError(t('error_loading_totp_settings'))
    } finally {
      setTotpSettingsLoading(false)
    }
  }

  const handleActivateTotp = async () => {
    if (ALLOW_IN_MVP) {
      setShowTotpPhase2Modal(true)
    } else {
      if (!totpSettings) return
      try {
        setTotpLoading(true)
        const result = await profileService.activateTotp(totpSettings.secret, totpCode)
        if (result.success) {
          setIsTotpEnabled(true)
          setRecoveryCodes(result.recovery_codes || [])
          toastService.showSuccess(t('two_factor_enabled'))
        } else {
          toastService.showError(t('error_enabling_two_factor'))
        }
      } catch (error) {
        toastService.showError(t('error_enabling_two_factor'))
      } finally {
        setTotpLoading(false)
      }
    }
  }

  const handleDeactivateTotp = async () => {
    try {
      setTotpLoading(true)
      await profileService.deactivateTotp()
      setIsTotpEnabled(false)
      setTotpSettings(null)
      setRecoveryCodes(null)
      setTotpCode('')
      toastService.showSuccess(t('two_factor_disabled'))
    } catch (error) {
      toastService.showError(t('error_disabling_two_factor'))
    } finally {
      setTotpLoading(false)
    }
  }

  const handleCopyToken = () => {
    if (!isExternalIntegrationsModuleEnabled || !formData.auth_token) {
      return
    }
    copyToClipboard(formData.auth_token)
    toastService.showSuccess(t('auth_token_copied'))
  }

  const handleDelete = () => {
    openConfirmDialog({
      title: t('confirm'),
      message: t('confirm_delete_account') || 'Da li zaista želite da obrišete svoj nalog? Ova akcija je trajna i ne može se poništiti.',
      confirmText: t('delete_account') || 'Obriši nalog',
      variant: 'danger',
      onConfirm: () => {
        // TODO: Implement account deletion
        toastService.showInfo(t('account_deletion_not_implemented'))
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('edit_profile')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSave()
            }}
          >
            <div className="row">
              <div className="col-12 col-md-6">
                {/* Email */}
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Email Confirm */}
                {showEmailConfirm && (
                  <div className="mb-3">
                    <label htmlFor="email_confirm" className="form-label">
                      {t('confirm_email')}
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email_confirm ? 'is-invalid' : ''}`}
                      id="email_confirm"
                      value={formData.email_confirm || ''}
                      onChange={(e) => handleInputChange('email_confirm', e.target.value)}
                    />
                    {errors.email_confirm && <div className="invalid-feedback">{errors.email_confirm}</div>}
                  </div>
                )}

                {/* Password */}
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    {t('password')}
                  </label>
                  <div className="input-group">
                    <input
                      type={revealPassword ? 'text' : 'password'}
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder={profile?.has_usable_password ? t('enter_new_password') : t('password')}
                      autoComplete="new-password"
                    />
                    <button className="btn btn-outline-secondary" type="button" onClick={() => setRevealPassword(!revealPassword)}>
                      <i className={revealPassword ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                    </button>
                  </div>
                  {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                </div>

                {/* Password Confirm */}
                <div className="mb-3">
                  <label htmlFor="password_confirm" className="form-label">
                    {t('confirm_password')}
                  </label>
                  <div className="input-group">
                    <input
                      type={revealPasswordConfirm ? 'text' : 'password'}
                      className={`form-control ${errors.password_confirm ? 'is-invalid' : ''}`}
                      id="password_confirm"
                      value={formData.password_confirm}
                      onChange={(e) => handleInputChange('password_confirm', e.target.value)}
                      placeholder={t('confirm_new_password')}
                      autoComplete="new-password"
                    />
                    <button className="btn btn-outline-secondary" type="button" onClick={() => setRevealPasswordConfirm(!revealPasswordConfirm)}>
                      <i className={revealPasswordConfirm ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
                    </button>
                  </div>
                  {errors.password_confirm && <div className="invalid-feedback d-block">{errors.password_confirm}</div>}
                </div>

                {/* First Name */}
                <div className="mb-3">
                  <label htmlFor="first_name" className="form-label">
                    {t('first_name')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                  />
                </div>

                {/* Last Name */}
                <div className="mb-3">
                  <label htmlFor="last_name" className="form-label">
                    {t('last_name')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                  />
                </div>

                {isExternalIntegrationsModuleEnabled && (
                  <div className="mb-3">
                    <label htmlFor="auth_token" className="form-label">
                      {t('api_auth_token')}
                    </label>
                    <div className="input-group">
                      <input type="text" className="form-control" id="auth_token" value={formData.auth_token} readOnly />
                      {(hasAuthToken || formData.auth_token) && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={handleShowToken}
                          title={t('show_token')}
                          disabled={authTokenLoading}
                        >
                          <i className={`bi ${authTokenLoading ? 'bi-hourglass-split' : 'bi-eye'}`}></i>
                        </button>
                      )}
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleCopyToken}
                        title={t('copy_token')}
                        disabled={!formData.auth_token}
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleGenerateToken}
                        title={t('generate_new_token')}
                        disabled={hasAuthToken && !formData.auth_token}
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </button>
                    </div>
                    <small className="form-text text-muted">{t('token_change_warning')}</small>
                  </div>
                )}
              </div>

              <div className="col-12 col-md-6">
                {/* Two-Factor Authentication (TOTP) - React-like accordion */}
                <div className="totp-accordion mb-3">
                  <div
                    className={`header ${totpOpen ? 'open' : ''}`}
                    onClick={() => {
                      setTotpOpen(!totpOpen)
                      if (!isTotpEnabled && !totpSettings && !totpOpen) loadTotp()
                    }}
                  >
                    <span>{t('two_factor_authentication')}</span>
                    <div className="ms-auto d-flex align-items-center gap-2">
                      <span className={`badge ${isTotpEnabled ? 'bg-success' : 'bg-secondary'}`}>{isTotpEnabled ? t('enabled') : t('disabled')}</span>
                      <i className={`bi ${totpOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                  </div>

                  {totpOpen && (
                    <div className="body">
                      {!isTotpEnabled ? (
                        <>
                          {totpSettings ? (
                            <div className="row g-3 align-items-start">
                              <div className="col-12">
                                <div className="qr-container mb-3">
                                  <div className="qr-inner" dangerouslySetInnerHTML={{ __html: totpSettings.qr_svg }} />
                                </div>
                                <p className="text-muted mb-2">{t('scan_qr_and_enter_code')}</p>
                                <p className="small mb-3">
                                  <strong>{t('totp_secret')}:</strong> <code>{totpSettings.secret}</code>. {t('you_can_store_secret_for_reinstall')}
                                </p>
                                <div className="input-group mt-2" style={{ maxWidth: 420 }}>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t('enter_totp_code') || ''}
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value)}
                                  />
                                  <button type="button" className="btn btn-primary" onClick={handleActivateTotp} disabled={!totpCode}>
                                    {t('enable')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-3">
                              {totpSettingsLoading ? (
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">{t('loading')}</span>
                                </div>
                              ) : (
                                <button type="button" className="btn btn-outline-secondary" onClick={loadTotp}>
                                  {t('setup_two_factor')}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* MFA je omogućen - prikaži status i opcije za deaktivaciju */}
                          <label className="d-block mb-2">{t('two_factor_authentication')}</label>

                          {recoveryCodes && recoveryCodes.length > 0 && (
                            <div className="alert alert-warning" role="alert">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              {t('recovery_codes_warning')}
                            </div>
                          )}

                          {recoveryCodes && recoveryCodes.length > 0 && (
                            <div className="d-flex flex-row align-items-start mb-3">
                              <ul className="list-group w-50">
                                {recoveryCodes.map((code, index) => {
                                  if (index % 2 === 0) {
                                    return (
                                      <li key={code} className="list-group-item d-flex justify-content-around align-items-center">
                                        <code>{code}</code>
                                        {recoveryCodes[index + 1] && <code>{recoveryCodes[index + 1]}</code>}
                                      </li>
                                    )
                                  }
                                  return null
                                })}
                              </ul>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ms-2"
                                onClick={() => {
                                  copyToClipboard(recoveryCodes.join('\n'))
                                  toastService.showSuccess(t('recovery_codes_copied'))
                                }}
                                title={t('copy_recovery_codes')}
                              >
                                <i className="bi bi-clipboard me-1"></i>
                                {t('copy_recovery_codes')}
                              </button>
                            </div>
                          )}

                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleDeactivateTotp} disabled={totpLoading}>
                            <i className="bi bi-trash me-1"></i>
                            {t('disable_two_factor_authentication')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-danger" onClick={handleDelete}>
          <i className="bi bi-trash me-1"></i>
          {t('delete')}
        </button>
        <div className="ms-auto" />
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t('cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              {t('saving')}
            </>
          ) : (
            t('save')
          )}
        </button>
      </Modal.Footer>

      <ConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText || undefined}
        variant={confirmDialog.variant}
        onConfirm={handleDialogConfirm}
        onCancel={closeConfirmDialog}
      />

      <Phase2NoticeModal show={showTotpPhase2Modal} onClose={() => setShowTotpPhase2Modal(false)} />
    </Modal>
  )
}
